import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';
import _uniq from 'lodash/uniq';
import * as yup from 'yup';

import {
  FullTalentQuestionDTO,
  FullTalentQuestionDTOType,
  PlatformLocale,
  TalentQuestionsResponseDTO
} from '@/api/generated';
import ArrowBackIcon from '@/assets/icons/arrow-back.svg?react';
import { CommonFormFooter } from '@/components/Form/Form';
import { useFieldPrefix } from '@/hooks/useFieldPrefix';
import { WarningProvider } from '@/hooks/useWarnings';
import {
  PROCESS_SETTINGS_STEP_KEYS,
  TALENT_REGISTRATION_FORM_DEFAULT_LANGUAGE,
  TALENT_REGISTRATION_FORM_LANGUAGES,
  TALENT_REGISTRATION_FORM_TABS,
  TALENT_REGISTRATION_FORM_TABS_LIST
} from '@/root/linker';
import { borders } from '@/theme/constants';
import typography from '@/theme/typography';
import { shortId } from '@/utils/shortId';
import AddQuestionButton from '@/views/TalentRegistrationForm/AddQuestionButton';
import { DEFAULT_LANGUAGE_CODE } from '@/views/TalentRegistrationForm/constants';
import LanguageListSection from '@/views/TalentRegistrationForm/LanguageListSection';
import {
  getTalentRegistrationFormValidationSchema,
  getTalentRegistrationFormWarningSchema
} from '@/views/TalentRegistrationForm/schema';
import TalentRegistrationFormContent, {
  TalentRegistrationFormInstance
} from '@/views/TalentRegistrationForm/TalentRegistrationFormContent';

type InvitationLanguage = Values<typeof PlatformLocale>;

const DEFAULT_LANGUAGE = TALENT_REGISTRATION_FORM_DEFAULT_LANGUAGE as InvitationLanguage;

interface TalentRegistrationFormProps {
  toggleFullScreenMode?: (isFullScreen: boolean) => void;
  organizationSlug?: string;
}

const TalentRegistrationForm: FC<TalentRegistrationFormProps> = ({ toggleFullScreenMode, organizationSlug }) => {
  const { spacing } = useTheme();
  const { t } = useTranslation(['settings', 'validation']);
  const activeFormContentRef = useRef<TalentRegistrationFormInstance>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<InvitationLanguage>(DEFAULT_LANGUAGE);
  const [activeLanguages, setActiveLanguages] = useState<InvitationLanguage[]>([]);
  const [activeTab, setActiveTab] = useState(TALENT_REGISTRATION_FORM_TABS.EXTERNAL);

  const { watch } = useFormContext();
  const { getPrefixedFieldName } = useFieldPrefix();

  const translations = watch(getPrefixedFieldName('translations'), null);

  const extractedLanguages = useMemo(
    () => _uniq([DEFAULT_LANGUAGE_CODE, ...Object.keys(translations || {})]) as InvitationLanguage[],
    [translations]
  );

  useEffect(() => {
    if (!extractedLanguages) return;

    setActiveLanguages(extractedLanguages);
  }, [extractedLanguages?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const warningValidationSchema = useMemo(
    () =>
      yup.object().shape({
        [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TALENT_REGISTRATION]: getTalentRegistrationFormWarningSchema()
      }),
    []
  );

  const handleAddQuestion = useCallback(() => {
    activeFormContentRef.current?.addQuestion({
      id: shortId(),
      type: FullTalentQuestionDTOType.TEXT,
      isRequired: true,
      isTalentEditable: false,
      isActive: true,
      isKnockout: false
    } as FullTalentQuestionDTO);
  }, []);

  return (
    <WarningProvider warningSchema={warningValidationSchema}>
      <Stack direction="row" justifyContent="space-between" my={1}>
        <Button
          onClick={() => toggleFullScreenMode?.(false)}
          sx={{
            '& .ArrowBackIcon-root': {
              marginInlineEnd: 1.5
            },
            '& .MuiTypography-root': {
              color: 'text.primary'
            },
            '& .MuiDivider-root': {
              mx: 1,
              borderColor: 'text.primary'
            }
          }}
        >
          <ArrowBackIcon className="ArrowBackIcon-root" />
          <Typography variant="body1">{t('processSettings.form.talentRegistration.settings')}</Typography>
          <Divider orientation="vertical" />
          <Typography variant="body1" sx={{ fontWeight: typography.fontWeightBold }}>
            {t('processSettings.form.talentRegistration.formEditor')}
          </Typography>
        </Button>
        <AddQuestionButton onAddQuestion={handleAddQuestion} />
      </Stack>
      <Stack direction="row" sx={{ flexGrow: 1, flexBasis: 0 }}>
        <Stack px={2} py={3} height="100%" bgcolor="background.default" sx={{ textAlign: 'center' }}>
          <LanguageListSection
            value={activeLanguages}
            onChange={languages => setActiveLanguages(languages as InvitationLanguage[])}
            onSelectedChange={selectedLanguage => setSelectedLanguage(selectedLanguage as InvitationLanguage)}
            selectedValue={selectedLanguage}
            options={TALENT_REGISTRATION_FORM_LANGUAGES}
            validation={getTalentRegistrationFormValidationSchema(t) as yup.ObjectSchema<TalentQuestionsResponseDTO>}
          />
        </Stack>
        <Stack p={2} pt={0} flexGrow={1} bgcolor="grey.100" minHeight="100%" overflow="auto">
          <Tabs
            value={activeTab}
            onChange={(_event, value) => setActiveTab(value)}
            sx={{
              mt: 2,
              minHeight: spacing(5),
              position: 'sticky',
              top: spacing(2),
              zIndex: 1,
              bgcolor: 'grey.100',
              overflow: 'visible',
              '&::before': {
                content: '""',
                position: 'absolute',
                width: '100%',
                bottom: '100%',
                left: 0,
                right: 0,
                height: spacing(2),
                bgcolor: 'grey.100'
              },
              '& .MuiTab-root': {
                padding: spacing(1.5, 2),
                minHeight: 'unset',
                textTransform: 'none',
                alignItems: 'start',
                borderTopRightRadius: spacing(0.5),
                borderTopLeftRadius: spacing(0.5),
                lineHeight: 1
              },
              '& .MuiTab-root:not(.Mui-selected)': {
                fontWeight: typography.fontWeightRegular
              },
              '& .MuiTab-root.Mui-selected': {
                fontWeight: typography.fontWeightMedium,
                bgcolor: 'primary.100'
              },
              '& .MuiTabs-flexContainer': {
                borderBottom: borders.default,
                borderColor: 'grey.400',
                gap: 3
              }
            }}
          >
            {TALENT_REGISTRATION_FORM_TABS_LIST.map(formKey => (
              <Tab label={t(`processSettings.form.talentRegistration.tabs.${formKey}`)} value={formKey} key={formKey} />
            ))}
          </Tabs>
          {TALENT_REGISTRATION_FORM_TABS_LIST.map(
            formKey =>
              activeTab === formKey && (
                <TalentRegistrationFormContent
                  key={formKey}
                  ref={activeFormContentRef}
                  activeTab={formKey}
                  languages={activeLanguages}
                  activeLanguage={selectedLanguage}
                  tabKeys={TALENT_REGISTRATION_FORM_TABS_LIST}
                  organizationSlug={organizationSlug}
                />
              )
          )}
        </Stack>
      </Stack>
      <CommonFormFooter
        sx={{
          mt: 0,
          mx: -3,
          mb: -3,
          position: 'sticky',
          bottom: -24,
          zIndex: 1,
          bgcolor: 'grey.100'
        }}
        submitText={t('processSettings.form.talentRegistration.apply')}
      />
    </WarningProvider>
  );
};

export default TalentRegistrationForm;
